import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useShallow } from 'zustand/shallow';
import { useEditorStore, useRectificationStore } from '../../../stores';
import { MeshBVH, acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from 'three-mesh-bvh';

THREE.Mesh.prototype.raycast = acceleratedRaycast;
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;

const CM_TO_UNIT = 1; // scene is in centimeters

const BlockingZoneHandler = () => {
    const [scene, shadedMesh, controls, camera, renderer, setInteractiveObjects, setPaintingCursor] = useEditorStore(
        useShallow((s) => {
            const refs = s.sceneRefs || {};
            return [refs.scene, refs.shadedMesh, refs.controls, refs.camera, refs.renderer, s.setInteractiveObjects, s.setPaintingCursor];
        })
    );

    const [blockingZone, setBlockingZone] = useRectificationStore(
        useShallow((s) => [s.blockingZone, s.setBlockingZone])
    );

    const overlayRef = useRef(null); // single overlay mesh visualizing the block mask
    const paintingRef = useRef(false);
    const brushRadiusRef = useRef(blockingZone?.radius ?? 5);
    const lastPaintPosRef = useRef(null); // Track last paint position for interpolation
    const lastGeometryRef = useRef(null); // track geometry changes for overlay invalidation

    // keep latest brush radius
    useEffect(() => {
        brushRadiusRef.current = blockingZone?.radius ?? 5;
    }, [blockingZone?.radius]);

    function ensureOverlay() {
        // Check if geometry has changed (e.g., after alignment transformation)
        const geometryChanged = lastGeometryRef.current &&
            shadedMesh &&
            shadedMesh.geometry.attributes.position !== lastGeometryRef.current;

        // Invalidate cached overlay if geometry changed
        if (geometryChanged && overlayRef.current) {
            if (overlayRef.current.parent) {
                overlayRef.current.parent.remove(overlayRef.current);
            }
            overlayRef.current.geometry?.dispose();
            overlayRef.current.material?.dispose();
            overlayRef.current = null;
        }

        if (overlayRef.current || !shadedMesh) return overlayRef.current;

        const base = shadedMesh.geometry;
        lastGeometryRef.current = base.attributes.position; // track current geometry
        const overlayGeom = new THREE.BufferGeometry();
        if (base.index) overlayGeom.setIndex(base.index);
        overlayGeom.setAttribute('position', base.attributes.position);
        overlayGeom.setAttribute('normal', base.attributes.normal);

        const count = base.attributes.position.count;
        const paint = new Float32Array(count);
        overlayGeom.setAttribute('paint', new THREE.BufferAttribute(paint, 1));

        const mat = new THREE.ShaderMaterial({
            uniforms: {
                uColor: { value: new THREE.Color(1, 0, 0) }, // red
            },
            vertexShader: `
                precision highp float;
                attribute float paint;
                varying float vPaint;
                void main(){
                    vPaint = paint;
                    vec3 pos = position + normal * 0.0006;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
                }
            `,
            fragmentShader: `
                precision highp float;
                uniform vec3 uColor;
                varying float vPaint;
                void main(){
                    float alpha = smoothstep(0.49, 0.51, vPaint);
                    if (alpha < 0.01) discard;
                    vec3 col = pow(clamp(uColor, 0.0, 1.0), vec3(0.5));
                    gl_FragColor = vec4(col, alpha);
                    #include <colorspace_fragment>
                    #include <dithering_fragment>
                }
            `,
            transparent: true,
            blending: THREE.NormalBlending,
            depthTest: true,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -2.1,
        });

        const overlay = new THREE.Mesh(overlayGeom, mat);
        overlay.name = 'BlockingZoneOverlay';
        overlay.renderOrder = 5e5 + 777;
        shadedMesh.add(overlay);
        overlayRef.current = overlay;
        return overlay;
    }

    function removeOverlay() {
        const overlay = overlayRef.current;
        if (overlay) {
            overlay.parent?.remove(overlay);
            overlay.material?.dispose?.();
            overlayRef.current = null;
        }
    }

    function syncOverlayFromMask() {
        if (!shadedMesh) return;
        const overlay = ensureOverlay();
        if (!overlay) return;
        const mask = useRectificationStore.getState().blockingZone?.mask;
        const count = shadedMesh.geometry.attributes.position.count;
        const paintAttr = overlay.geometry.getAttribute('paint');
        if (!mask || mask.length !== count) {
            // Clear overlay if mask not ready
            paintAttr.array.fill(0);
            paintAttr.needsUpdate = true;
            return;
        }
        paintAttr.array.set(mask);
        paintAttr.needsUpdate = true;
    }

    function paintAt(hitPointWorld, isDragging = false) {
        if (!shadedMesh) return;

        const baseGeom = shadedMesh.geometry;
        const basePos = baseGeom.attributes.position;
        const vertexCount = basePos.count;

        // ensure mask buffer exists
        let mask = useRectificationStore.getState().blockingZone?.mask;
        if (!mask || mask.length !== vertexCount) {
            mask = new Float32Array(vertexCount);
        }

        const brushRadiusWorld = (brushRadiusRef.current ?? 5) * CM_TO_UNIT;
        const brushRadiusSq = brushRadiusWorld * brushRadiusWorld;

        // Interpolate between last and current position when dragging
        const strokePositions = [];
        
        if (isDragging && lastPaintPosRef.current) {
            // Calculate distance between last and current position
            const lastPos = lastPaintPosRef.current;
            const distance = lastPos.distanceTo(hitPointWorld);
            
            // Determine number of interpolation steps based on distance
            // More steps for longer distances to ensure smooth coverage
            const stepSize = brushRadiusWorld * 0.2; // Step at 20% of brush radius for good overlap
            const numSteps = Math.max(1, Math.ceil(distance / stepSize));
            
            // Interpolate positions
            for (let step = 0; step <= numSteps; step++) {
                const t = step / numSteps;
                const interpPos = new THREE.Vector3().lerpVectors(lastPos, hitPointWorld, t);
                strokePositions.push(interpPos);
            }
        } else {
            // Single click or first drag point
            strokePositions.push(hitPointWorld);
        }
        
        // Store current position for next iteration
        lastPaintPosRef.current = hitPointWorld.clone();
        
        // Apply paint at each interpolated position
        let changed = false;
        const mode = useRectificationStore.getState().blockingZone?.mode;
        
        // Adjust accumulation strength based on number of positions
        // This prevents over-accumulation when many interpolated positions overlap
        const accumulationStrength = isDragging ? 0.5 / Math.sqrt(strokePositions.length) : 0.5;
        
        for (const strokePos of strokePositions) {
            // Convert stroke position to local space
            const strokeLocal = new THREE.Vector3().copy(strokePos);
            shadedMesh.worldToLocal(strokeLocal);
            
            // Paint at this position
            for (let i = 0; i < vertexCount; i++) {
                const dx = basePos.getX(i) - strokeLocal.x;
                const dy = basePos.getY(i) - strokeLocal.y;
                const dz = basePos.getZ(i) - strokeLocal.z;
                const d2 = dx*dx + dy*dy + dz*dz;
                if (d2 >= brushRadiusSq) continue; // outside brush, skip

                // Smoother falloff function for better edges
                const nd2 = d2 / brushRadiusSq;     // (d/r)^2
                const t   = 1.0 - nd2;              // 1 - (d/r)^2
                
                // Use smoothstep function for softer edges instead of quartic
                const falloff = t * t * (3.0 - 2.0 * t); // Smoothstep function
                const add = falloff * accumulationStrength;

                const old = mask[i];
                let nu = old;
                if (mode === 'erase') {
                    nu = old - add;
                    if (nu < 0) nu = 0;
                } else {
                    // Use max operation for more consistent results
                    nu = Math.max(old, old + add * (1.0 - old)); // Blend towards 1.0
                    if (nu > 1) nu = 1;
                }
                if (nu !== old) { 
                    mask[i] = nu; 
                    changed = true; 
                }
            }
        }

        if (changed) {
            setBlockingZone({ mask });
            syncOverlayFromMask();
        }
    }

    // mount / unmount
    useEffect(() => {
        if (!scene || !shadedMesh || !camera || !renderer) return;

        if (!shadedMesh.geometry.boundsTree) shadedMesh.geometry.computeBoundsTree();

        ensureOverlay();
        syncOverlayFromMask();
        setInteractiveObjects([shadedMesh]);

        const dom = renderer.domElement;
        const ray = new THREE.Raycaster();
        const ndc = new THREE.Vector2();

        function intersect(ev) {
            const r = dom.getBoundingClientRect();
            ndc.set(((ev.clientX - r.left)/r.width)*2-1, -((ev.clientY - r.top)/r.height)*2+1);
            ray.setFromCamera(ndc, camera);
            const hits = ray.intersectObject(shadedMesh, true);
            return hits[0] || null;
        }

        const onKeyDown = (ev) => {
            if (ev.shiftKey) setPaintingCursor({ enabled: true });
        };

        const onKeyUp = (ev) => {
            if (ev.key === 'Shift') setPaintingCursor({ enabled: false });
        };

        const onPointerDown = (ev) => {
            if (!ev.shiftKey || ev.button !== 0) return; // paint with shift+LMB
            const mode = useRectificationStore.getState().blockingZone?.mode;
            if (mode !== 'paint' && mode !== 'erase') return;
            const hit = intersect(ev);
            if (!hit) return;
            paintingRef.current = true;
            if (controls) controls.enabled = false;
            lastPaintPosRef.current = null; // Reset for new stroke
            paintAt(hit.point, false);
        };

        const onPointerMove = (ev) => {
            // cursor handling with Shift key
            if (!ev.shiftKey) return;
            // if (!useEditorStore.getState().paintingCursor.enabled) setPaintingCursor({ enabled: true });

            const hit = intersect(ev);
            const radiusWorld = (brushRadiusRef.current ?? 5) * CM_TO_UNIT;
            const canvas = renderer?.domElement;
            let radiusPx = Math.max(2, (brushRadiusRef.current ?? 5) * 16);
            if (canvas && camera && hit) {
                if (camera.isPerspectiveCamera) {
                    const fovRad = (camera.fov * Math.PI) / 180;
                    const distance = camera.position.distanceTo(hit.point);
                    const worldScreenHeight = 2 * distance * Math.tan(fovRad / 2);
                    const pxPerWorld = canvas.clientHeight / Math.max(worldScreenHeight, 1e-6);
                    radiusPx = Math.max(2, Math.round(radiusWorld * pxPerWorld));
                } else if (camera.isOrthographicCamera) {
                    const effHeight = Math.abs((camera.top - camera.bottom) / Math.max(camera.zoom || 1, 1e-6));
                    const pxPerWorld = canvas.clientHeight / Math.max(effHeight, 1e-6);
                    radiusPx = Math.max(2, Math.round(radiusWorld * pxPerWorld));
                }
            }
            const mode = useRectificationStore.getState().blockingZone?.mode;
            const color = mode === 'erase' ? '#00a2ff' : '#ff0000';
            setPaintingCursor({ enabled: true, radiusPx, color });

            if (!paintingRef.current) return;
            if (!hit) return;
            if (ev.buttons & 2) return; // ignore RMB drag
            paintAt(hit.point, true); // Pass isDragging = true for move events
        };

        const onPointerUp = () => {
            paintingRef.current = false;
            lastPaintPosRef.current = null; // Reset for next stroke
            if (controls) controls.enabled = true;
        };

        dom.addEventListener('pointerdown', onPointerDown);
        dom.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        return () => {
            dom.removeEventListener('pointerdown', onPointerDown);
            dom.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            if (controls) controls.enabled = true;
            removeOverlay();
            setInteractiveObjects([]);
            setPaintingCursor({ enabled: false });
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
        };
    }, [scene, shadedMesh, camera, renderer, controls]);

    // respond to mask changes from outside (e.g., undo/redo)
    useEffect(() => { if (!shadedMesh) return; ensureOverlay(); syncOverlayFromMask(); }, [blockingZone?.mask, shadedMesh]);

    return null;
};

export default BlockingZoneHandler;